import React, { useState, useEffect, useContext } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import { AppContext } from '../../application/provider';

// ─── Catálogos ───────────────────────────────────────────────────────────────
const TIPOS_COMPROBANTE = [
    { value: 1, label: 'Factura' },
    { value: 2, label: 'Boleta' },
    { value: 3, label: 'Recibo por Honorarios' },
    { value: 4, label: 'Nota de Crédito' },
    { value: 5, label: 'Liquidación' },
];

const TIPOS_INGRESO = [
    { value: 1, label: 'Servicio' },
    { value: 2, label: 'Producto' },
    { value: 3, label: 'Alquiler' },
    { value: 4, label: 'Consultoría' },
    { value: 5, label: 'Otro' },
];

const ESTADOS_PAGO = [
    { value: 0, label: 'Pendiente' },
    { value: 1, label: 'Pago Parcial' },
    { value: 2, label: 'Pagado' },
    { value: 3, label: 'Anulado' },
];

const ESTADOS_CONTABILIDAD = [
    { value: 0, label: 'Pendiente Revisión' },
    { value: 1, label: 'Revisado' },
    { value: 2, label: 'Contabilizado' },
];

// ─── Initial state ────────────────────────────────────────────────────────────
const initialForm = {
    empresa_id: '',
    cliente: '',
    fecha_emision: '',
    fecha_vencimiento: '',
    tipo_comprobante: '',
    serie_comprobante: '',
    numero_comprobante: '',
    tipo_ingreso: '',
    monto_total: '',
    monto_pagado: '',
    monto_detraccion: '',
    estado_pago: 0,
    entidad_financiera_id: '',
    fecha_pago: '',
    numero_operacion: '',
    estado_contabilidad: 0,
    fecha_registro_contable: '',
    observacion: '',
};

// ─── Component ────────────────────────────────────────────────────────────────
const ModalFinanzasCobranzas = ({ isOpen, onClose, cobranza, onSave }) => {
    const [state] = useContext(AppContext);
    const [form, setForm] = useState(initialForm);
    const [saving, setSaving] = useState(false);
    const isEditing = !!cobranza;

    useEffect(() => {
        if (isOpen) {
            if (cobranza) {
                setForm({
                    empresa_id: cobranza.empresa_id ?? '',
                    cliente: cobranza.cliente ?? '',
                    fecha_emision: cobranza.fecha_emision ?? '',
                    fecha_vencimiento: cobranza.fecha_vencimiento ?? '',
                    tipo_comprobante: cobranza.tipo_comprobante ?? '',
                    serie_comprobante: cobranza.serie_comprobante ?? '',
                    numero_comprobante: cobranza.numero_comprobante ?? '',
                    tipo_ingreso: cobranza.tipo_ingreso ?? '',
                    monto_total: cobranza.monto_total ?? '',
                    monto_pagado: cobranza.monto_pagado ?? '',
                    monto_detraccion: cobranza.monto_detraccion ?? '',
                    estado_pago: cobranza.estado_pago ?? 0,
                    entidad_financiera_id: cobranza.entidad_financiera_id ?? '',
                    fecha_pago: cobranza.fecha_pago ?? '',
                    numero_operacion: cobranza.numero_operacion ?? '',
                    estado_contabilidad: cobranza.estado_contabilidad ?? 0,
                    fecha_registro_contable: cobranza.fecha_registro_contable ?? '',
                    observacion: cobranza.observacion ?? '',
                });
            } else {
                setForm(initialForm);
            }
        }
    }, [isOpen, cobranza]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        // Validaciones básicas
        if (!form.empresa_id || !form.cliente.trim() || !form.fecha_emision ||
            !form.fecha_vencimiento || !form.tipo_comprobante ||
            !form.tipo_ingreso || !form.monto_total) {
            alert('Complete los campos obligatorios (*).');
            return;
        }

        const currentPerfilId = Number(state.user?.perfil_id || state.perfil_id);
        const payload = {
            ...form,
            perfil_id: currentPerfilId,
            usuario_id: state.user?.usuario_id || state.usuario_id,
            cliente: form.cliente.toUpperCase(),
        };

        if (isEditing) {
            payload.cobranza_id = cobranza.cobranza_id;
        }

        setSaving(true);
        try {
            await onSave(payload, isEditing);
            onClose();
        } catch (err) {
            alert(err?.response?.data?.message || 'Error al guardar la cobranza.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} className="custom-modal" backdrop="static" size="lg">
            <ModalHeader className="modal-header">
                <div className="titulo">
                    <h1>{isEditing ? 'EDITAR COBRANZA' : 'NUEVA COBRANZA'}</h1>
                    <Button className="close-btn" onClick={onClose}>X</Button>
                </div>
            </ModalHeader>

            <div className="modal-body-footer">
                <ModalBody className="modal-body">
                    <Form className="fila" onSubmit={e => e.preventDefault()}>

                        {/* Empresa */}
                        <FormGroup>
                            <Label>Empresa ID: *</Label>
                            <Input
                                className="form-control"
                                type="number"
                                name="empresa_id"
                                value={form.empresa_id}
                                onChange={handleChange}
                                placeholder="ID de empresa"
                            />
                        </FormGroup>

                        {/* Cliente */}
                        <FormGroup>
                            <Label>Cliente: *</Label>
                            <Input
                                className="form-control"
                                type="text"
                                name="cliente"
                                value={form.cliente}
                                onChange={handleChange}
                                placeholder="Nombre del cliente"
                                maxLength={150}
                            />
                        </FormGroup>

                        {/* Fechas */}
                        <FormGroup>
                            <Label>Fecha de Emisión: *</Label>
                            <Input
                                className="form-control"
                                type="date"
                                name="fecha_emision"
                                value={form.fecha_emision}
                                onChange={handleChange}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Fecha de Vencimiento: *</Label>
                            <Input
                                className="form-control"
                                type="date"
                                name="fecha_vencimiento"
                                value={form.fecha_vencimiento}
                                onChange={handleChange}
                            />
                        </FormGroup>

                        {/* Comprobante */}
                        <FormGroup>
                            <Label>Tipo de Comprobante: *</Label>
                            <Input
                                className="form-control"
                                type="select"
                                name="tipo_comprobante"
                                value={form.tipo_comprobante}
                                onChange={handleChange}
                            >
                                <option value="">— Seleccione —</option>
                                {TIPOS_COMPROBANTE.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </Input>
                        </FormGroup>

                        <FormGroup>
                            <Label>Serie Comprobante:</Label>
                            <Input
                                className="form-control"
                                type="text"
                                name="serie_comprobante"
                                value={form.serie_comprobante}
                                onChange={handleChange}
                                placeholder="Ej: F001"
                                maxLength={10}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Número de Comprobante:</Label>
                            <Input
                                className="form-control"
                                type="text"
                                name="numero_comprobante"
                                value={form.numero_comprobante}
                                onChange={handleChange}
                                placeholder="Ej: 00001234"
                                maxLength={20}
                            />
                        </FormGroup>

                        {/* Tipo de Ingreso */}
                        <FormGroup>
                            <Label>Tipo de Ingreso: *</Label>
                            <Input
                                className="form-control"
                                type="select"
                                name="tipo_ingreso"
                                value={form.tipo_ingreso}
                                onChange={handleChange}
                            >
                                <option value="">— Seleccione —</option>
                                {TIPOS_INGRESO.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </Input>
                        </FormGroup>

                        {/* Montos */}
                        <FormGroup>
                            <Label>Monto Total: *</Label>
                            <Input
                                className="form-control"
                                type="number"
                                name="monto_total"
                                value={form.monto_total}
                                onChange={handleChange}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                            />
                        </FormGroup>

                        {isEditing && (
                            <FormGroup>
                                <Label>Monto Pagado:</Label>
                                <Input
                                    className="form-control"
                                    type="number"
                                    name="monto_pagado"
                                    value={form.monto_pagado}
                                    onChange={handleChange}
                                    placeholder="0.00"
                                    min="0"
                                    step="0.01"
                                />
                            </FormGroup>
                        )}

                        <FormGroup>
                            <Label>Monto Detracción:</Label>
                            <Input
                                className="form-control"
                                type="number"
                                name="monto_detraccion"
                                value={form.monto_detraccion}
                                onChange={handleChange}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                            />
                        </FormGroup>

                        {/* Estado Pago (solo edición) */}
                        {isEditing && (
                            <>
                                <FormGroup>
                                    <Label>Estado de Pago:</Label>
                                    <Input
                                        className="form-control"
                                        type="select"
                                        name="estado_pago"
                                        value={form.estado_pago}
                                        onChange={handleChange}
                                    >
                                        {ESTADOS_PAGO.map(e => (
                                            <option key={e.value} value={e.value}>{e.label}</option>
                                        ))}
                                    </Input>
                                </FormGroup>

                                <FormGroup>
                                    <Label>Fecha de Pago:</Label>
                                    <Input
                                        className="form-control"
                                        type="date"
                                        name="fecha_pago"
                                        value={form.fecha_pago}
                                        onChange={handleChange}
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label>Número de Operación:</Label>
                                    <Input
                                        className="form-control"
                                        type="text"
                                        name="numero_operacion"
                                        value={form.numero_operacion}
                                        onChange={handleChange}
                                        placeholder="Número de operación bancaria"
                                        maxLength={50}
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label>Estado Contabilidad:</Label>
                                    <Input
                                        className="form-control"
                                        type="select"
                                        name="estado_contabilidad"
                                        value={form.estado_contabilidad}
                                        onChange={handleChange}
                                    >
                                        {ESTADOS_CONTABILIDAD.map(e => (
                                            <option key={e.value} value={e.value}>{e.label}</option>
                                        ))}
                                    </Input>
                                </FormGroup>

                                <FormGroup>
                                    <Label>Fecha Registro Contable:</Label>
                                    <Input
                                        className="form-control"
                                        type="date"
                                        name="fecha_registro_contable"
                                        value={form.fecha_registro_contable}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </>
                        )}

                        {/* Entidad Financiera */}
                        <FormGroup>
                            <Label>Entidad Financiera ID:</Label>
                            <Input
                                className="form-control"
                                type="number"
                                name="entidad_financiera_id"
                                value={form.entidad_financiera_id}
                                onChange={handleChange}
                                placeholder="ID entidad financiera"
                            />
                        </FormGroup>

                        {/* Observación */}
                        <FormGroup>
                            <Label>Observación:</Label>
                            <Input
                                className="form-control"
                                type="textarea"
                                name="observacion"
                                rows={3}
                                value={form.observacion}
                                onChange={handleChange}
                                placeholder="Observaciones adicionales..."
                            />
                        </FormGroup>

                        {/* Botón */}
                        <FormGroup style={{ marginTop: '18px' }}>
                            <div className="button-container">
                                <Button
                                    className="button-custom"
                                    onClick={handleSubmit}
                                    disabled={saving}
                                >
                                    {saving ? 'REGISTRANDO...' : 'REGISTRAR'}
                                </Button>
                            </div>
                        </FormGroup>

                    </Form>
                </ModalBody>
            </div>
        </Modal>
    );
};

export default ModalFinanzasCobranzas;